/* ═══════════════════════════════════════════════════════════════════════════
   THE PICTURE: WHAT MAY BE UPLOADED, AND WHAT MAY BE DONE TO IT

   The social planner could pick from the platform's own pack and nothing
   else. A business with a photograph of its own salon had no way to use it,
   and no way to put a word on it — which is most of what a salon's post
   actually is.

   This module is the rules half. It knows nothing about React and nothing
   about the server; it answers three questions and the screens ask them:

     may this file be uploaded, and if not, why
     what does this picture look like with these adjustments
     where does this line of text sit, and will anybody be able to read it

   ── One filter string, two renderers ─────────────────────────────────────

   The editor shows the picture in the DOM and then exports it through a
   canvas. Those are two different renderers and the classic way to get this
   wrong is to write the adjustments twice — a CSS filter for the screen and
   a pixel loop for the export — at which point what the tenant approved and
   what they publish are two different pictures.

   So `filterOf` produces ONE string, the browser's own `filter` syntax, and
   it is set on the element's style AND on the canvas context. Chromium
   honours `ctx.filter` with the same grammar, so the export is the preview
   by construction rather than by care.

   ── Why the text is a layer and not baked in ─────────────────────────────

   Because the tenant will want to move it, and because the same picture goes
   to channels of different shapes. A word centred on a square is off the
   bottom of a 9:16. Layers are positioned in FRACTIONS of the frame — never
   pixels — so the same layout survives being re-rendered at any size, which
   is exactly what happens between a 360px preview and a 1080px export.

   ── Video ────────────────────────────────────────────────────────────────

   Uploadable, and not editable. A canvas composer cannot put a word on a
   moving picture without a frame-by-frame pipeline that does not belong in a
   browser, so the studio refuses video rather than pretending: `editable()`
   answers false and the screen says the clip goes up as it is. Offering a
   text box that silently did nothing would be worse than not offering one.
   ═══════════════════════════════════════════════════════════════════════ */

/* ── What may be uploaded ─────────────────────────────────────────────────
   Deliberately narrow. Every format here is one every browser can decode and
   every channel accepts; a TIFF or a HEIC would upload cleanly and then fail
   silently somewhere the tenant cannot see. */
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

/* Caps, in bytes. The picture limit is generous for a photograph off a
   telephone and mean for a screenshot of a spreadsheet, which is the right
   way round. The video limit is what a short clip costs; anything longer is
   a file transfer and not a post. */
export const MAX_IMAGE = 12 * 1024 * 1024;
export const MAX_VIDEO = 200 * 1024 * 1024;

export const kindOf = type => {
  const t = String(type || '').toLowerCase();
  if (IMAGE_TYPES.includes(t)) return 'image';
  if (VIDEO_TYPES.includes(t)) return 'video';
  return null;
};

/** Only a picture can be composed on. See the note at the top. */
export const editable = kind => kind === 'image';

/**
 * Whether this file may be uploaded, and if not, a sentence saying why.
 *
 * The sentence is the whole point. "Upload failed" tells the tenant to try
 * again with the same file; "That is a HEIC — save it as a JPEG first" tells
 * them what to do, and they do it.
 */
export function checkFile(file) {
  if (!file) return { ok: false, why: 'No file was chosen.' };
  const kind = kindOf(file.type);
  if (!kind) {
    return {
      ok: false,
      why: `${file.type || 'That file'} cannot go on a post. Pictures: JPEG, `
        + 'PNG, WebP or GIF. Video: MP4, MOV or WebM. A HEIC from an iPhone '
        + 'needs saving as a JPEG first.',
    };
  }
  const cap = kind === 'image' ? MAX_IMAGE : MAX_VIDEO;
  if (file.size > cap) {
    return {
      ok: false,
      why: `That ${kind} is ${megabytes(file.size)} and the limit is `
        + `${megabytes(cap)}. ${kind === 'image'
          ? 'Most phones can export a smaller copy.'
          : 'A post is a short clip; anything longer is a file transfer.'}`,
    };
  }
  return { ok: true, kind, why: null };
}

export const megabytes = n =>
  (Number(n || 0) / (1024 * 1024)).toFixed(1).replace('.0', '') + ' MB';

/* ── The adjustments ──────────────────────────────────────────────────────
   Six, and no more. Every one of them is a thing somebody can see themselves
   doing to a photograph — "brighter", "warmer", "less colour" — rather than
   a parameter they have to learn. A panel of twenty sliders is a panel
   nobody touches. */
export const ADJUSTMENTS = [
  { id: 'brightness', word: 'Brightness', min: 50, max: 150, mid: 100, unit: '%' },
  { id: 'contrast', word: 'Contrast', min: 50, max: 150, mid: 100, unit: '%' },
  { id: 'saturate', word: 'Colour', min: 0, max: 200, mid: 100, unit: '%' },
  { id: 'warmth', word: 'Warmth', min: 0, max: 100, mid: 0, unit: '%' },
  { id: 'fade', word: 'Fade', min: 0, max: 100, mid: 0, unit: '%' },
  { id: 'blur', word: 'Blur', min: 0, max: 12, mid: 0, unit: 'px' },
];

export const NEUTRAL = Object.fromEntries(ADJUSTMENTS.map(a => [a.id, a.mid]));

/** Whether anything has actually been changed, so the screen can say so. */
export const touched = (adj = {}) =>
  ADJUSTMENTS.some(a => Number(adj[a.id] ?? a.mid) !== a.mid);

/**
 * The adjustments as one CSS filter string.
 *
 * Used for the live element AND for `ctx.filter` on the export canvas, which
 * is the entire reason this is a function rather than two pieces of styling
 * in two components. See the note at the top of the file.
 *
 * `warmth` and `fade` are not filter primitives — they are the two things
 * people actually ask for, expressed in primitives that exist: warmth is a
 * touch of sepia, fade is contrast pulled down with brightness lifted, which
 * is what a faded print looks like.
 */
export function filterOf(adj = {}) {
  const v = id => Number(adj[id] ?? NEUTRAL[id]);
  const fade = v('fade');
  const out = [];
  /* Fade is folded into brightness and contrast rather than applied after
     them, so the two cannot fight each other. */
  const bright = v('brightness') + fade * 0.12;
  const contrast = v('contrast') - fade * 0.25;
  if (bright !== 100) out.push(`brightness(${round(bright)}%)`);
  if (contrast !== 100) out.push(`contrast(${round(contrast)}%)`);
  if (v('saturate') !== 100) out.push(`saturate(${round(v('saturate'))}%)`);
  if (v('warmth') > 0) out.push(`sepia(${round(v('warmth') * 0.6)}%)`);
  if (v('blur') > 0) out.push(`blur(${round(v('blur'))}px)`);
  return out.length ? out.join(' ') : 'none';
}

const round = n => Math.round(Number(n) * 100) / 100;

/* ── The presets ──────────────────────────────────────────────────────────
   Named after what they are for rather than after a mood. "Bright and clean"
   is a thing a salon wants its photograph to be; "Aurora" is a thing
   somebody has to press to find out about. */
export const PRESETS = [
  { id: 'none', word: 'As taken', adj: {} },
  { id: 'clean', word: 'Bright & clean', adj: { brightness: 108, contrast: 106, saturate: 106 } },
  { id: 'warm', word: 'Warm', adj: { warmth: 35, saturate: 112, brightness: 104 } },
  { id: 'quiet', word: 'Quiet', adj: { saturate: 72, contrast: 96, fade: 25 } },
  { id: 'mono', word: 'Black & white', adj: { saturate: 0, contrast: 112 } },
  { id: 'punch', word: 'Punchy', adj: { contrast: 126, saturate: 128, brightness: 98 } },
];

export const presetAdj = id => ({
  ...NEUTRAL,
  ...(PRESETS.find(p => p.id === id)?.adj || {}),
});

/* ── The words on the picture ─────────────────────────────────────────────
   Positioned in fractions of the frame, never pixels — see the note at the
   top. A layer laid out on a 360px preview has to land in the same place on
   a 1080px export, and the only way that is true by construction is if the
   numbers never mention pixels at all. */
export const FONTS = [
  { id: 'sans', word: 'Plain', css: '"Inter", system-ui, -apple-system, sans-serif' },
  { id: 'serif', word: 'Serif', css: '"Instrument Serif", Georgia, serif' },
];

/* Black and white first, because the overwhelming majority of legible text
   on a photograph is one or the other. The rest are the platform's own. */
export const INKS = ['#ffffff', '#141413', '#c9a227', '#b24a3c', '#2f6f6b', '#3a4a7a'];

export const newLayer = (text = 'Your words here') => ({
  id: 'L' + Math.random().toString(36).slice(2, 8),
  text,
  /* Middle of the frame, and a size that is readable on a phone: a tenth of
     the frame's width per line of cap height. */
  x: 0.5,
  y: 0.5,
  size: 0.085,
  ink: '#ffffff',
  font: 'sans',
  weight: 700,
  align: 'center',
  /* Legibility, which is the part everybody forgets: white words on a
     photograph are white words on whatever the photograph happens to be, and
     half the time that is a window. `band` is a translucent bar behind the
     line; `shadow` is the lighter-touch version. */
  band: 0,
  shadow: true,
  /* Rotation in degrees, because a sale flash sits at an angle and a price
     does not. */
  turn: 0,
});

export const clamp01 = n => Math.min(1, Math.max(0, Number(n) || 0));

/** A layer's lines, so the same wrapping drives the preview and the export. */
export const linesOf = layer =>
  String(layer?.text ?? '').split('\n').map(l => l.replace(/\s+$/, ''));

/**
 * What is wrong with this composition, in the tenant's words.
 *
 * Not validation — nothing here blocks anything. These are the four mistakes
 * that are invisible on a bright desktop monitor and obvious on a telephone
 * in daylight, and saying them beforehand is cheaper than saying them after.
 */
export function problems(layers = [], adj = {}) {
  const out = [];
  layers.forEach(l => {
    if (!String(l.text || '').trim()) {
      out.push('A line of text on the picture is empty.');
      return;
    }
    if (l.size < 0.04) {
      out.push(`“${short(l.text)}” is small enough to disappear on a phone.`);
    }
    /* White on nothing. Almost always the reason a caption is unreadable. */
    if (l.ink === '#ffffff' && !l.band && !l.shadow) {
      out.push(`“${short(l.text)}” is white with nothing behind it — it will `
        + 'vanish wherever the picture is pale.');
    }
    if (clamp01(l.y) < 0.06 || clamp01(l.y) > 0.94) {
      out.push(`“${short(l.text)}” is against the edge, where a feed may crop it.`);
    }
  });
  if (Number(adj.blur ?? 0) > 6 && layers.length === 0) {
    out.push('The picture is blurred and there is nothing written on it.');
  }
  return [...new Set(out)];
}

const short = t => {
  const s = String(t || '').split('\n')[0].trim();
  return s.length > 22 ? s.slice(0, 21) + '…' : s;
};

/**
 * The size an export should be, given the frame's aspect.
 *
 * 1080 on the long edge: what every channel in data/channels.js wants, and
 * small enough that the data URL does not become the reason a draft cannot be
 * saved. Bigger would be re-compressed by the channel anyway.
 */
export function exportSize(ratio = 1, long = 1080) {
  const r = Number(ratio) || 1;
  return r >= 1
    ? { w: long, h: Math.round(long / r) }
    : { w: Math.round(long * r), h: long };
}
